import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, AlertCircle, Clock, FileText } from 'lucide-react';
import api from '../services/api';

const TYPE_META = {
    invoice_upcoming:  { icon: Clock,       iconColor: 'text-accent',        borderColor: 'border-l-accent' },
    invoice_due:       { icon: AlertCircle, iconColor: 'text-status-pending', borderColor: 'border-l-status-pending' },
    invoice_overdue:   { icon: FileText,    iconColor: 'text-status-overdue', borderColor: 'border-l-status-overdue' },
    reminder_upcoming: { icon: Bell,        iconColor: 'text-accent',        borderColor: 'border-l-accent' },
    reminder_due:      { icon: Bell,        iconColor: 'text-status-pending', borderColor: 'border-l-status-pending' },
};

const DEFAULT_META = { icon: Bell, iconColor: 'text-text-muted', borderColor: 'border-l-surface-border' };

const formatRelativeTime = (iso) => {
    const now = new Date();
    const d = new Date(iso);
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const calDays = Math.round((todayStart - dStart) / 86_400_000);
    if (calDays === 0) return 'Today';
    if (calDays === 1) return 'Yesterday';
    if (calDays < 7) return `${calDays} days ago`;
    const weeks = Math.floor(calDays / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
};

const normalizeNotification = (n) => ({
    ...n,
    time: formatRelativeTime(n.created_at),
    ...(TYPE_META[n.type] ?? DEFAULT_META),
});

const QUERY_KEY = ['notifications'];

export const useNotifications = () => {
    return useInfiniteQuery({
        queryKey: QUERY_KEY,
        queryFn: async ({ pageParam }) => {
            const data = await api.getNotifications({
                per_page: 30,
                ...(pageParam ? { cursor: pageParam } : {}),
            });
            return {
                ...data,
                items: (data.items ?? []).map(normalizeNotification),
            };
        },
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.pagination?.next_cursor ?? null,
    });
};

export const useMarkNotificationRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (uid) => api.markNotificationRead(uid),
        onMutate: async (uid) => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });
            const previous = queryClient.getQueryData(QUERY_KEY);
            queryClient.setQueryData(QUERY_KEY, (old) =>
                old
                    ? { ...old, pages: old.pages.map((page) => ({ ...page, items: page.items.map((n) => (n.uid === uid ? { ...n, read: true } : n)) })) }
                    : old
            );
            return { previous };
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previous);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
};

export const useMarkAllNotificationsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => api.markAllNotificationsRead(),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });
            const previous = queryClient.getQueryData(QUERY_KEY);
            queryClient.setQueryData(QUERY_KEY, (old) =>
                old
                    ? { ...old, pages: old.pages.map((page) => ({ ...page, items: page.items.map((n) => ({ ...n, read: true })) })) }
                    : old
            );
            return { previous };
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previous);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
};

export const useDeleteNotification = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (uid) => api.deleteNotification(uid),
        onMutate: async (uid) => {
            await queryClient.cancelQueries({ queryKey: QUERY_KEY });
            const previous = queryClient.getQueryData(QUERY_KEY);
            queryClient.setQueryData(QUERY_KEY, (old) =>
                old
                    ? { ...old, pages: old.pages.map((page) => ({ ...page, items: page.items.filter((n) => n.uid !== uid) })) }
                    : old
            );
            return { previous };
        },
        onError: (_, __, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previous);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
};

export const useClearNotifications = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (filter) => api.clearNotifications(filter),
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
    });
};
